import React, { useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from 'canvas-confetti';

// Self-contained embeddable semi-circle spinner component
// This component includes all necessary logic and can receive crop data from props

export interface CropData {
  name: string;
  state: string;
  price: number;
  harvestYield: number;
  highYield: number;
  lowYield?: number;
  cost: number;
  avgYield: number;
  landownerMsgGoodYield?: string;
  landownerMsgLowYield?: string;
  farmerMsgGoodYield?: string;
  farmerMsgLowYield?: string;
  insuranceBasePrice?: number;
  insuranceHarvestPrice?: number;
}

export interface EmbedConfig {
  crops?: CropData[];
  primaryColor?: string;
  showInsurance?: boolean;
  onCalculate?: (rent: number, crop: CropData) => void;
}

// Validate and parse crop data from CMS (handles string values)
const validateCrop = (crop: Partial<CropData>): CropData | null => {
  const price = parseFloat(String(crop.price)) || 0;
  const harvestYield = parseFloat(String(crop.harvestYield)) || 0;
  
  if (!crop.name || price <= 0 || harvestYield <= 0) {
    return null;
  }
  
  return {
    name: String(crop.name).trim(),
    state: String(crop.state || '').trim(),
    price,
    harvestYield,
    highYield: parseFloat(String(crop.highYield)) || harvestYield * 1.1,
    lowYield: crop.lowYield != null ? parseFloat(String(crop.lowYield)) || 0 : undefined,
    cost: parseFloat(String(crop.cost)) || 0,
    avgYield: parseFloat(String(crop.avgYield)) || harvestYield * 0.9,
    landownerMsgGoodYield: crop.landownerMsgGoodYield ? String(crop.landownerMsgGoodYield).trim() : undefined,
    landownerMsgLowYield: crop.landownerMsgLowYield ? String(crop.landownerMsgLowYield).trim() : undefined,
    farmerMsgGoodYield: crop.farmerMsgGoodYield ? String(crop.farmerMsgGoodYield).trim() : undefined,
    farmerMsgLowYield: crop.farmerMsgLowYield ? String(crop.farmerMsgLowYield).trim() : undefined,
    insuranceBasePrice: crop.insuranceBasePrice != null ? parseFloat(String(crop.insuranceBasePrice)) : undefined,
    insuranceHarvestPrice: crop.insuranceHarvestPrice != null ? parseFloat(String(crop.insuranceHarvestPrice)) : undefined,
  };
};

// Fallback UI when data is missing or invalid
const MissingDataFallback = () => (
  <div style={{
    padding: '40px',
    textAlign: 'center',
    background: '#FEF2F2',
    borderRadius: '16px',
    border: '1px solid #FECACA',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }}>
    <p style={{ fontSize: '16px', color: '#991B1B', marginBottom: '8px', margin: '0 0 8px 0' }}>
      Unable to load spinner data
    </p>
    <p style={{ fontSize: '12px', color: '#7F1D1D', margin: 0 }}>
      Check CMS field configuration
    </p>
  </div>
);

// Default crop data if none provided
const defaultCropData: CropData[] = [
  { name: "Corn", state: "Iowa", price: 4.85, harvestYield: 185, highYield: 210, lowYield: 150, cost: 625, avgYield: 175 },
  { name: "Soybeans", state: "Illinois", price: 12.50, harvestYield: 52, highYield: 62, lowYield: 40, cost: 385, avgYield: 48 },
  { name: "Canola", state: "North Dakota", price: 14.25, harvestYield: 42, highYield: 50, lowYield: 32, cost: 320, avgYield: 38 },
];

// Green confetti with emoji shapes for rent calculation celebration
const fireGreenConfetti = () => {
  const greenColors = [
    '#2E7D32', '#81C784', '#A5D6A7', '#C8E6C9', '#B2DFDB',
  ];

  const defaults = {
    colors: greenColors,
    spread: 80,
    ticks: 100,
    gravity: 1.2,
    decay: 0.94,
    startVelocity: 30,
  };

  confetti({ ...defaults, particleCount: 100, angle: 60, origin: { x: 0.25, y: 0.7 } });
  confetti({ ...defaults, particleCount: 100, angle: 120, origin: { x: 0.75, y: 0.7 } });

  // Crop emoji bursts
  try {
    const cropShapes = [
      confetti.shapeFromText({ text: '🌽', scalar: 6 }),
      confetti.shapeFromText({ text: '🌾', scalar: 6 }),
      confetti.shapeFromText({ text: '🌿', scalar: 6 }),
      confetti.shapeFromText({ text: '🌻', scalar: 6 }),
      confetti.shapeFromText({ text: '🍃', scalar: 6 }),
    ];

    const emojiDefaults = {
      spread: 70,
      ticks: 120,
      gravity: 1.0,
      decay: 0.94,
      startVelocity: 25,
      shapes: cropShapes,
      scalar: 6,
    };

    confetti({ ...emojiDefaults, particleCount: 15, angle: 55, origin: { x: 0.3, y: 0.7 } });
    confetti({ ...emojiDefaults, particleCount: 15, angle: 125, origin: { x: 0.7, y: 0.7 } });
  } catch { /* shapeFromText may not be available in older canvas-confetti */ }
};

// Dynamic segment color function (cycles through 3-color pattern)
const getSegmentColor = (idx: number) => {
  const colors = [
    "#348C1D",  // Dark green
    "rgba(52, 140, 29, 0.4)",  // Medium green
    "rgba(52, 140, 29, 0.2)",  // Light green
  ];
  return colors[idx % 3];
};

const getSegmentTextColor = (idx: number) => idx % 3 === 0 ? "#FFFFFF" : "#348C1D";

// Custom PNG icon imports (Vite will inline as base64 for standalone bundle)
import PriceIcon from "@/assets/icons/Price.png";
import HarvestYieldIcon from "@/assets/icons/Harvest_Yield.png";
import HighYieldIcon from "@/assets/icons/High_Yield.png";
import CostIcon from "@/assets/icons/Cost.png";
import AvgYieldIcon from "@/assets/icons/AVG_Yield.png";

// 3D card styles for embed
const card3DStyleEmbed = (isLow: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: isLow
    ? "linear-gradient(180deg, rgba(255,235,235,0.88) 0%, rgba(255,245,245,0.75) 100%)"
    : "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.75) 100%)",
  boxShadow: isLow
    ? "0 4px 12px rgba(220,38,38,0.12), 0 1px 3px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)"
    : "0 4px 12px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
  border: isLow ? "1px solid rgba(220,38,38,0.25)" : "1px solid rgba(52,140,29,0.22)",
  backdropFilter: "blur(8px)",
  borderRadius: '8px',
  padding: '10px 14px',
  transition: 'box-shadow 0.2s, transform 0.2s',
  cursor: 'default',
});

const card3DHoverStyleEmbed = (isLow: boolean) => ({
  boxShadow: isLow
    ? "0 6px 18px rgba(220,38,38,0.18), 0 2px 6px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.7)"
    : "0 6px 18px rgba(52,140,29,0.18), 0 2px 6px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.7)",
  transform: "translateY(-2px) scale(1.04)",
});

const SPINNER_STATE_KEY = "rentcurves_embed_spinner_state";
const SPINNER_STATE_TTL = 10 * 60 * 1000; // 10 minutes

const SemiCircleSpinnerEmbed = ({ config }: { config?: EmbedConfig }) => {
  // Validate incoming crops data
  const rawCrops = config?.crops || defaultCropData;
  const validatedCrops = rawCrops.map(crop => validateCrop(crop)).filter((crop): crop is CropData => crop !== null);
  const crops = validatedCrops.length > 0 ? validatedCrops : defaultCropData;
  const shouldShowFallback = config?.crops && config.crops.length > 0 && validatedCrops.length === 0;

  // Dynamic segment count based on actual crops - no repeats
  const SEGMENT_COUNT = crops.length;
  const SEGMENT_ANGLE = 360 / SEGMENT_COUNT;

  // Use crops directly - no cycling needed
  const cropCycle = crops;

  // Rehydrate saved state from localStorage
  const savedState = (() => {
    try {
      const raw = localStorage.getItem(SPINNER_STATE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.ts > SPINNER_STATE_TTL) {
        localStorage.removeItem(SPINNER_STATE_KEY);
        return null;
      }
      return parsed as { selectedIndex: number; rentResult: number; isLowYield: boolean; cropName: string; ts: number };
    } catch { return null; }
  })();

  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex ?? 0);
  const [rotationAngle, setRotationAngle] = useState(
    savedState ? (savedState.selectedIndex * SEGMENT_ANGLE) + SEGMENT_ANGLE / 2 - SEGMENT_ANGLE / 2 : -SEGMENT_ANGLE / 2
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showResult, setShowResult] = useState(!!savedState?.rentResult);
  const [rentResult, setRentResult] = useState<number | null>(savedState?.rentResult ?? null);
  const [includeInsurance, setIncludeInsurance] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isLowYield = includeInsurance;
  const [resultIsLowYield, setResultIsLowYield] = useState(savedState?.isLowYield ?? false);
  const [audioMuted, setAudioMuted] = useState(false);

  const dragStartX = useRef(0);
  const rotationAtDragStart = useRef(0);
  const lastX = useRef(0);
  const lastTime = useRef(0);
  const velocity = useRef(0);
  const animationRef = useRef<number | null>(null);

  // Audio tick refs
  const audioCtxRef = useRef<AudioContext | null>(null);
  const lastSegmentRef = useRef<number>(-1);

  const getOrCreateAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch { /* graceful fallback */ }
    }
    return audioCtxRef.current;
  }, []);

  const playTick = useCallback((ctx: AudioContext | null) => {
    if (!ctx || audioMuted) return;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = "square";
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.03);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.03);
    } catch { /* ignore */ }
  }, [audioMuted]);

  const checkTickSound = useCallback((currentRotation: number) => {
    const seg = Math.floor(((currentRotation % 360 + 360) % 360) / SEGMENT_ANGLE);
    if (seg !== lastSegmentRef.current) {
      lastSegmentRef.current = seg;
      playTick(audioCtxRef.current);
    }
  }, [SEGMENT_ANGLE, playTick]);

  const currentCrop = cropCycle[selectedIndex];

  // Re-dispatch saved state for chart widget on mount
  useEffect(() => {
    if (savedState?.rentResult && savedState?.cropName) {
      window.dispatchEvent(new CustomEvent('rentcurves:calculated', {
        detail: {
          rentValue: savedState.rentResult,
          cropName: savedState.cropName,
          isLowYield: savedState.isLowYield,
          state: crops[savedState.selectedIndex]?.state || '',
        },
      }));
    }
  }, []); // Run once on mount

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get crop at top-center position
  const getCropIndexAtTop = useCallback((rotation: number): number => {
    const rot = (((rotation % 360) + 360) % 360);
    const halfSegment = SEGMENT_ANGLE / 2;
    const idxRaw = Math.round((rot - halfSegment) / SEGMENT_ANGLE);
    return ((idxRaw % SEGMENT_COUNT) + SEGMENT_COUNT) % SEGMENT_COUNT;
  }, [SEGMENT_ANGLE, SEGMENT_COUNT]);

  // Snap to nearest segment center
  const snapToSegment = useCallback((currentRotation: number): number => {
    const halfSegment = SEGMENT_ANGLE / 2;
    return Math.round((currentRotation + halfSegment) / SEGMENT_ANGLE) * SEGMENT_ANGLE - halfSegment;
  }, [SEGMENT_ANGLE]);

  // Animate to target rotation (snap after drag)
  const animateToRotation = useCallback((targetRotation: number, duration: number = 0.3) => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    const startRotation = rotationAngle;
    const distance = targetRotation - startRotation;
    const durationMs = duration * 1000;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentRotation = startRotation + distance * eased;

      setRotationAngle(currentRotation);
      checkTickSound(currentRotation);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setRotationAngle(targetRotation);
        const newIndex = getCropIndexAtTop(targetRotation);
        setSelectedIndex(newIndex);
        setIsSpinning(false);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, [rotationAngle, getCropIndexAtTop, checkTickSound]);

  // Handle spin
  const handleSpin = useCallback(() => {
    if (isSpinning || isDragging) return;
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    getOrCreateAudioCtx();
    setIsSpinning(true);
    setShowResult(false);
    setRentResult(null);

    const spins = 3 + Math.random() * 3;
    const randomOffset = Math.random() * 360;
    const targetRotation = rotationAngle + (spins * 360) + randomOffset;
    const snappedTarget = snapToSegment(targetRotation);

    const spinDuration = 3;
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    const startRotation = rotationAngle;
    const distance = snappedTarget - startRotation;
    const durationMs = spinDuration * 1000;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentRotation = startRotation + distance * eased;

      setRotationAngle(currentRotation);
      checkTickSound(currentRotation);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setRotationAngle(snappedTarget);
        const newIndex = getCropIndexAtTop(snappedTarget);
        setSelectedIndex(newIndex);
        setIsSpinning(false);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, [isSpinning, isDragging, rotationAngle, snapToSegment, getCropIndexAtTop, getOrCreateAudioCtx, checkTickSound]);

  // Pointer handlers
  const handlePointerDown = (e: React.PointerEvent) => {
    if (isSpinning) return;
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    getOrCreateAudioCtx();

    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    dragStartX.current = e.clientX;
    rotationAtDragStart.current = rotationAngle;
    lastX.current = e.clientX;
    lastTime.current = Date.now();
    velocity.current = 0;
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStartX.current;
    const sensitivity = 0.5;
    const newRotation = rotationAtDragStart.current + deltaX * sensitivity;

    const now = Date.now();
    const timeDelta = now - lastTime.current;
    if (timeDelta > 0) {
      velocity.current = ((e.clientX - lastX.current) / timeDelta) * 1000;
    }
    lastX.current = e.clientX;
    lastTime.current = now;

    setRotationAngle(newRotation);
  };

  const handlePointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    let finalRotation = rotationAngle;
    if (Math.abs(velocity.current) > 50) {
      finalRotation += velocity.current * 0.5 * 0.15;
    }

    const snappedRotation = snapToSegment(finalRotation);
    animateToRotation(snappedRotation, 0.3);
    setShowResult(false);
    setRentResult(null);
  };

  // Calculate rent (local fallback)
  const calculateRentLocal = useCallback(() => {
    const crop = currentCrop;
    const grossRevenue = crop.price * crop.harvestYield;
    const insuranceDeduction = includeInsurance ? 25 : 0;
    const netReturn = grossRevenue - crop.cost - insuranceDeduction;
    const yieldFactor = crop.harvestYield / crop.avgYield;
    return Math.max(0, Math.round(netReturn * 0.35 * yieldFactor));
  }, [currentCrop, includeInsurance]);

  const handleCalculate = async () => {
    setIsCalculating(true);
    setShowResult(false);
    setResultIsLowYield(isLowYield);

    try {
      const requestBody: any = {
        FieldSize: 1, // Default to 1 acre - may need to be configurable
        HighYield: currentCrop.highYield,
        AverageYield: currentCrop.avgYield,
        LocalAgPrice: currentCrop.price,
        ProductionCost: currentCrop.cost,
        HarvestYield: harvestYieldDisplayValue, // Uses lowYield if isLowYield, otherwise harvestYield
      };

      // Include BasePrice and HarvestPrice when insurance is enabled
      if (includeInsurance) {
        if (currentCrop.insuranceBasePrice != null) {
          requestBody.BasePrice = currentCrop.insuranceBasePrice;
        }
        if (currentCrop.insuranceHarvestPrice != null) {
          requestBody.HarvestPrice = currentCrop.insuranceHarvestPrice;
        }
      }

      const response = await fetch(
        'https://tbnsiakswacbbqnzrdhd.supabase.co/functions/v1/calculate-rent',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Calculation failed');
      }

      const rent = data.TotalFieldRent;
      setRentResult(rent);
      setShowResult(true);
      if (!isLowYield) {
        fireGreenConfetti();
      }
      config?.onCalculate?.(rent, currentCrop);

      // Save state to localStorage for redirect persistence
      localStorage.setItem(SPINNER_STATE_KEY, JSON.stringify({
        selectedIndex, rentResult: rent, isLowYield, cropName: currentCrop.name, ts: Date.now(),
      }));

      // Dispatch event for chart widget
      window.dispatchEvent(new CustomEvent('rentcurves:calculated', {
        detail: {
          rentValue: rent,
          cropName: currentCrop.name,
          isLowYield: isLowYield,
          state: currentCrop.state,
        },
      }));
    } catch (error) {
      console.error('Rent calculation failed:', error);
      const rent = calculateRentLocal();
      setRentResult(rent);
      setShowResult(true);
      if (!isLowYield) {
        fireGreenConfetti();
      }
      config?.onCalculate?.(rent, currentCrop);

      localStorage.setItem(SPINNER_STATE_KEY, JSON.stringify({
        selectedIndex, rentResult: rent, isLowYield, cropName: currentCrop.name, ts: Date.now(),
      }));

      window.dispatchEvent(new CustomEvent('rentcurves:calculated', {
        detail: {
          rentValue: rent,
          cropName: currentCrop.name,
          isLowYield: isLowYield,
          state: currentCrop.state,
        },
      }));
    } finally {
      setIsCalculating(false);
    }
  };

  // SVG dimensions
  const width = 900;
  const height = 500;
  const outerRadius = 420;
  const innerRadius = 280;
  const centerX = width / 2;
  const centerY = height;

  const createArcPath = (index: number) => {
    const startAngle = 90 + index * SEGMENT_ANGLE;
    const endAngle = startAngle + SEGMENT_ANGLE;
    const toRad = (deg: number) => (deg * Math.PI) / 180;

    const x1 = centerX + outerRadius * Math.cos(toRad(startAngle));
    const y1 = centerY - outerRadius * Math.sin(toRad(startAngle));
    const x2 = centerX + outerRadius * Math.cos(toRad(endAngle));
    const y2 = centerY - outerRadius * Math.sin(toRad(endAngle));
    const x3 = centerX + innerRadius * Math.cos(toRad(endAngle));
    const y3 = centerY - innerRadius * Math.sin(toRad(endAngle));
    const x4 = centerX + innerRadius * Math.cos(toRad(startAngle));
    const y4 = centerY - innerRadius * Math.sin(toRad(startAngle));

    return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 0 0 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 0 1 ${x4} ${y4} Z`;
  };

  const getTextPosition = (index: number, wheelRotation: number) => {
    const midAngle = 90 + (index + 0.5) * SEGMENT_ANGLE;
    const textRadius = (outerRadius + innerRadius) / 2;
    const midRad = (midAngle * Math.PI) / 180;

    return {
      x: centerX + textRadius * Math.cos(midRad),
      y: centerY - textRadius * Math.sin(midRad),
      rotation: -wheelRotation,
    };
  };

  // Determine harvest yield display value (changes with insurance toggle)
  const harvestYieldDisplayValue = isLowYield && currentCrop.lowYield != null
    ? currentCrop.lowYield
    : currentCrop.harvestYield;

  // Inline styles
  const styles = {
    container: {
      all: 'initial' as const,
      display: 'block',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      width: '100%',
      maxWidth: '900px',
      margin: '0 auto',
      boxSizing: 'border-box' as const,
      lineHeight: 1.5,
      color: '#374151',
      paddingBottom: '24px',
      overflow: 'visible' as const,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px',
      padding: '0 8px',
    },
    headerText: {
      fontSize: '14px',
      color: '#6B7280',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    spinButton: {
      fontSize: '14px',
      color: '#348C1D',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    wheelWrapper: isMobile ? {
      position: 'relative' as const,
      width: '100%',
      maxWidth: '520px',
      margin: '0 auto',
      aspectRatio: '900/500',
    } : {
      position: 'relative' as const,
      height: height + 120,
      overflow: 'visible' as const,
    },
    checkboxWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      margin: '12px 0 8px',
    },
    calculateButton: {
      width: '100%',
      marginTop: '8px',
      background: '#348C1D',
      color: '#ffffff',
      padding: '12px 40px',
      borderRadius: '50px',
      border: 'none',
      fontWeight: 600,
      fontSize: '16px',
      cursor: 'pointer',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      position: 'relative' as const,
      overflow: 'hidden' as const,
    },
    result: {
      maxWidth: '500px',
      margin: '24px auto',
      padding: '24px 32px',
      textAlign: 'center' as const,
      borderRadius: '16px',
      border: '1px solid',
    },
    resultGood: {
      background: '#F0FDF4',
      borderColor: '#BBF7D0',
    },
    resultLow: {
      background: '#FEF2F2',
      borderColor: '#FECACA',
    },
    resultLabel: {
      fontSize: '14px',
      color: '#6B7280',
      marginBottom: '8px',
    },
    resultValueGood: {
      fontSize: '48px',
      fontWeight: 700,
      color: '#2D8C2D',
    },
    resultValueLow: {
      fontSize: '48px',
      fontWeight: 700,
      color: '#DC2626',
    },
    resultUnit: {
      fontSize: '14px',
      color: '#6B7280',
      marginTop: '8px',
    },
    messageBox: {
      marginTop: '12px',
      padding: '12px',
      borderRadius: '8px',
      background: '#F9FAFB',
      textAlign: 'left' as const,
    },
    messageLabel: {
      fontSize: '11px',
      fontWeight: 600,
      color: '#6B7280',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      marginBottom: '4px',
    },
    messageText: {
      fontSize: '13px',
      color: '#374151',
      margin: 0,
    },
  };

  if (shouldShowFallback) {
    return <MissingDataFallback />;
  }

  // Input fields config with field-specific icons
  const inputFields = [
    { key: 'harvestYield', label: isLowYield ? 'Low Harvest Yield' : 'Harvest Yield', prefix: '', value: harvestYieldDisplayValue, icon: HarvestYieldIcon, isYield: isLowYield },
    { key: 'price', label: 'Crop Price', prefix: '$', value: currentCrop.price, icon: PriceIcon, isYield: false },
    { key: 'cost', label: 'Production Cost', prefix: '$', value: currentCrop.cost, icon: CostIcon, isYield: false },
    { key: 'avgYield', label: 'Average Yield', prefix: '', value: currentCrop.avgYield, icon: AvgYieldIcon, isYield: false },
    { key: 'highYield', label: 'Highest Yield', prefix: '', value: currentCrop.highYield, icon: HighYieldIcon, isYield: false },
  ];

  // 2-1-2 grid layout for inputs
  const inputSection = (
    <div style={{
      width: isMobile ? '100%' : '340px',
      maxWidth: isMobile ? '380px' : undefined,
      margin: isMobile ? '8px auto 0' : undefined,
    }}>
      {/* Row 1: Harvest Yield & Crop Price */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        {[inputFields[0], inputFields[1]].map(({ key, label, prefix, value, icon, isYield }) => {
          const showRedStyle = isYield && isLowYield;
          return (
            <div
              key={key}
              style={card3DStyleEmbed(showRedStyle)}
              onMouseEnter={(e) => {
                const s = card3DHoverStyleEmbed(showRedStyle);
                e.currentTarget.style.boxShadow = s.boxShadow;
                e.currentTarget.style.transform = s.transform;
              }}
              onMouseLeave={(e) => {
                const s = card3DStyleEmbed(showRedStyle);
                e.currentTarget.style.boxShadow = s.boxShadow;
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={icon} alt={label} style={{ width: '32px', height: '32px', objectFit: 'contain', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))' }} />
                <span style={{ fontSize: '13px', color: showRedStyle ? '#DC2626' : '#6B7280', fontWeight: showRedStyle ? 500 : 400 }}>{label}</span>
              </div>
              <span style={{ fontWeight: 700, fontSize: '15px', color: showRedStyle ? '#DC2626' : '#111827' }}>{prefix}{value}</span>
            </div>
          );
        })}
      </div>

      {/* Row 2: Production Cost (centered) */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
        <div style={{ width: '60%' }}>
          {(() => {
            const f = inputFields[2];
            return (
              <div
                style={card3DStyleEmbed(false)}
                onMouseEnter={(e) => {
                  const s = card3DHoverStyleEmbed(false);
                  e.currentTarget.style.boxShadow = s.boxShadow;
                  e.currentTarget.style.transform = s.transform;
                }}
                onMouseLeave={(e) => {
                  const s = card3DStyleEmbed(false);
                  e.currentTarget.style.boxShadow = s.boxShadow;
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img src={f.icon} alt={f.label} style={{ width: '32px', height: '32px', objectFit: 'contain', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))' }} />
                  <span style={{ fontSize: '13px', color: '#6B7280' }}>{f.label}</span>
                </div>
                <span style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>{f.prefix}{f.value}</span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Row 3: Average Yield & Highest Yield */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
        {[inputFields[3], inputFields[4]].map(({ key, label, prefix, value, icon }) => (
          <div
            key={key}
            style={card3DStyleEmbed(false)}
            onMouseEnter={(e) => {
              const s = card3DHoverStyleEmbed(false);
              e.currentTarget.style.boxShadow = s.boxShadow;
              e.currentTarget.style.transform = s.transform;
            }}
            onMouseLeave={(e) => {
              const s = card3DStyleEmbed(false);
              e.currentTarget.style.boxShadow = s.boxShadow;
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img src={icon} alt={label} style={{ width: '32px', height: '32px', objectFit: 'contain', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))' }} />
              <span style={{ fontSize: '13px', color: '#6B7280' }}>{label}</span>
            </div>
            <span style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>{prefix}{value}</span>
          </div>
        ))}
      </div>

      {config?.showInsurance !== false && (
        <div style={styles.checkboxWrapper}>
          <input
            type="checkbox"
            id="semicircle-insurance-embed"
            checked={includeInsurance}
            onChange={(e) => {
              setIncludeInsurance(e.target.checked);
              if (showResult) {
                const newRent = calculateRentLocal();
                setRentResult(newRent);
              }
            }}
            style={{ accentColor: '#348C1D' }}
          />
          <label htmlFor="semicircle-insurance-embed" style={{ fontSize: '12px', color: '#6B7280', cursor: 'pointer' }}>
            Show a Low Harvest Yield with Insurance
          </label>
        </div>
      )}

      <button
        onClick={handleCalculate}
        disabled={isCalculating}
        style={{
          ...styles.calculateButton,
          opacity: isCalculating ? 0.7 : 1,
          cursor: isCalculating ? 'wait' : 'pointer',
        }}
      >
        {/* Shimmer overlay */}
        {!isCalculating && (
          <span style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%)',
            animation: 'rc-sheen-slide 3s ease-in-out infinite',
          }} />
        )}
        {isCalculating ? (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <span style={{ display: 'inline-block', animation: 'rc-spin 1s linear infinite', width: '16px', height: '16px' }}>⚙️</span>
            Calculating...
          </span>
        ) : (
          'Calculate Rent'
        )}
      </button>

      {/* Inline keyframes for shimmer and spin */}
      <style>{`
        @keyframes rc-sheen-slide {
          0% { transform: translateX(-150%); }
          100% { transform: translateX(150%); }
        }
        @keyframes rc-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  // Dynamic messages for result card
  const landownerMsg = resultIsLowYield
    ? currentCrop.landownerMsgLowYield
    : currentCrop.landownerMsgGoodYield;
  const farmerMsg = resultIsLowYield
    ? currentCrop.farmerMsgLowYield
    : currentCrop.farmerMsgGoodYield;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={styles.headerText}>
            <span style={{ color: '#348C1D' }}>↻</span> Drag to spin the wheel
          </span>
          <button onClick={handleSpin} disabled={isSpinning} style={styles.spinButton}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
            {isSpinning ? 'Spinning...' : 'Spin'}
          </button>
        </div>
        <button
          onClick={() => setAudioMuted(prev => !prev)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#348C1D', padding: '4px', display: 'flex', alignItems: 'center' }}
          title={audioMuted ? 'Unmute' : 'Mute'}
        >
          {audioMuted ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          )}
        </button>
      </div>

      {/* Wheel */}
      <div style={styles.wheelWrapper}>
        <svg
          width="100%"
          height={isMobile ? '100%' : height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMax meet"
          style={{
            display: 'block',
            maxWidth: width,
            margin: '0 auto',
            cursor: isSpinning ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            touchAction: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <defs>
            <clipPath id="semicircle-clip-embed">
              <rect x="0" y="0" width={width} height={centerY} />
            </clipPath>
            <linearGradient id="bottom-fade-embed" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="white" stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>

            {/* Gold rim gradient */}
            <linearGradient id="gold-rim-embed" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4A84B" />
              <stop offset="30%" stopColor="#FFD700" />
              <stop offset="50%" stopColor="#FFF8DC" />
              <stop offset="70%" stopColor="#FFD700" />
              <stop offset="100%" stopColor="#B8860B" />
            </linearGradient>

            {/* Gold rim shimmer overlay */}
            <linearGradient id="gold-rim-shimmer-embed" gradientUnits="userSpaceOnUse"
              x1="0" y1="0" x2="900" y2="0">
              <stop offset="0%" stopColor="rgba(255,255,255,0)" />
              <stop offset="45%" stopColor="rgba(255,255,255,0)" />
              <stop offset="50%" stopColor="rgba(255,248,220,0.6)" />
              <stop offset="55%" stopColor="rgba(255,255,255,0)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              <animateTransform attributeName="gradientTransform"
                type="translate" from="-900 0" to="900 0"
                dur="4s" repeatCount="indefinite" />
            </linearGradient>

            {/* Segment depth gradients */}
            <radialGradient id="seg-depth-dark-embed" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.12)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0.15)" />
            </radialGradient>
            <radialGradient id="seg-depth-mid-embed" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.10)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0.08)" />
            </radialGradient>
            <radialGradient id="seg-depth-light-embed" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.06)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0.04)" />
            </radialGradient>
          </defs>

          {/* Glow shadow beneath spinner */}
          <ellipse
            cx={centerX}
            cy={centerY + 10}
            rx={outerRadius * 0.7}
            ry={30}
            fill="rgba(52,140,29,0.12)"
            filter="blur(15px)"
            style={{ pointerEvents: 'none' }}
          />

          <g clipPath="url(#semicircle-clip-embed)">
            {/* Outer gold rim ring */}
            <circle
              cx={centerX}
              cy={centerY}
              r={outerRadius + 4}
              fill="none"
              stroke="url(#gold-rim-embed)"
              strokeWidth="6"
              style={{ pointerEvents: 'none' }}
            />
            {/* Shimmer overlay on gold rim */}
            <circle
              cx={centerX}
              cy={centerY}
              r={outerRadius + 4}
              fill="none"
              stroke="url(#gold-rim-shimmer-embed)"
              strokeWidth="6"
              style={{ pointerEvents: 'none' }}
            />

            <g transform={`rotate(${rotationAngle}, ${centerX}, ${centerY})`}>
              {cropCycle.map((crop, idx) => {
                const textPos = getTextPosition(idx, rotationAngle);
                const textColor = getSegmentTextColor(idx);
                const depthGradient = idx % 3 === 0
                  ? "url(#seg-depth-dark-embed)"
                  : idx % 3 === 1
                    ? "url(#seg-depth-mid-embed)"
                    : "url(#seg-depth-light-embed)";

                return (
                  <g key={idx}>
                    {/* Base segment */}
                    <path
                      d={createArcPath(idx)}
                      fill={getSegmentColor(idx)}
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                    {/* Depth overlay */}
                    <path
                      d={createArcPath(idx)}
                      fill={depthGradient}
                      style={{ pointerEvents: 'none' }}
                    />
                    {/* Inner bevel highlight */}
                    <path
                      d={createArcPath(idx)}
                      fill="none"
                      stroke="rgba(255,255,255,0.15)"
                      strokeWidth="1"
                      style={{ pointerEvents: 'none' }}
                    />
                    <text
                      x={textPos.x}
                      y={textPos.y - 15}
                      textAnchor="middle"
                      fill={textColor}
                      fontSize="25"
                      fontWeight="600"
                      transform={`rotate(${textPos.rotation}, ${textPos.x}, ${textPos.y})`}
                      style={{
                        pointerEvents: 'none',
                        textShadow: idx % 3 === 0 ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      }}
                    >
                      {crop.name}
                    </text>
                    <text
                      x={textPos.x}
                      y={textPos.y + 25}
                      textAnchor="middle"
                      fill={textColor}
                      fontSize="18"
                      transform={`rotate(${textPos.rotation}, ${textPos.x}, ${textPos.y})`}
                      style={{
                        pointerEvents: 'none',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      }}
                    >
                      {crop.state.toUpperCase()}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Inner gold rim */}
            <circle
              cx={centerX}
              cy={centerY}
              r={innerRadius - 2}
              fill="none"
              stroke="url(#gold-rim-embed)"
              strokeWidth="3"
              style={{ pointerEvents: 'none' }}
            />
          </g>

          {/* Bottom fade */}
          <rect x={0} y={centerY - 50} width={width / 2} height={50} fill="url(#bottom-fade-embed)" style={{ pointerEvents: 'none' }} />
          <rect x={width / 2} y={centerY - 50} width={width / 2} height={50} fill="url(#bottom-fade-embed)" style={{ pointerEvents: 'none' }} />

          {/* Larger pointer with gold border */}
          <polygon
            points={`${centerX - 36},30 ${centerX + 36},30 ${centerX},105`}
            fill="#2E7D32"
            stroke="url(#gold-rim-embed)"
            strokeWidth="2.5"
            style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
          />
        </svg>

        {/* Desktop: Input fields inside the spinner */}
        {!isMobile && (
          <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 240 }}>
            {inputSection}
          </div>
        )}
      </div>

      {/* Mobile: Input fields below the spinner */}
      {isMobile && inputSection}

      {/* Result display */}
      <AnimatePresence>
        {showResult && rentResult !== null && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            style={{
              ...styles.result,
              ...(resultIsLowYield ? styles.resultLow : styles.resultGood),
            }}
          >
            <div style={styles.resultLabel}>Estimated Cash Rent for {currentCrop.name}</div>
            <div style={resultIsLowYield ? styles.resultValueLow : styles.resultValueGood}>
              ${rentResult.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={styles.resultUnit}>per acre</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '12px' }}>
              Based on {currentCrop.state} market conditions
              {includeInsurance && ' with crop insurance'}
            </div>

            {/* Dynamic landowner/farmer messages */}
            {landownerMsg && (
              <div style={styles.messageBox}>
                <div style={styles.messageLabel}>Landowner</div>
                <p style={styles.messageText}>{landownerMsg}</p>
              </div>
            )}
            {farmerMsg && (
              <div style={{ ...styles.messageBox, marginTop: '8px' }}>
                <div style={styles.messageLabel}>Farmer</div>
                <p style={styles.messageText}>{farmerMsg}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SemiCircleSpinnerEmbed;
